export const MIN_SPAN = 0.00005;

export const translateBounds = (bounds, dLat, dLng) => [
  [bounds[0][0] + dLat, bounds[0][1] + dLng],
  [bounds[1][0] + dLat, bounds[1][1] + dLng],
];

export const computeInitialBounds = (viewportBounds, imgWidth, imgHeight) => {
  const [[vS, vW], [vN, vE]] = viewportBounds;
  const centerLat = (vS + vN) / 2;
  const centerLng = (vW + vE) / 2;
  const cosLat = Math.cos((centerLat * Math.PI) / 180);
  const aspect = imgWidth / imgHeight;
  let lngSpan = (vE - vW) * (2 / 3);
  let latSpan = (lngSpan * cosLat) / aspect;
  const maxLatSpan = (vN - vS) * 0.8;
  if (latSpan > maxLatSpan) {
    latSpan = maxLatSpan;
    lngSpan = (latSpan * aspect) / cosLat;
  }
  return [
    [centerLat - latSpan / 2, centerLng - lngSpan / 2],
    [centerLat + latSpan / 2, centerLng + lngSpan / 2],
  ];
};

const OPPOSITE_CORNER = { nw: "se", ne: "sw", sw: "ne", se: "nw" };

export const cornerPositions = (bounds) => {
  const [[s, w], [n, e]] = bounds;
  return {
    nw: [n, w],
    ne: [n, e],
    sw: [s, w],
    se: [s, e],
  };
};

export const edgeMidpoints = (bounds) => {
  const [[s, w], [n, e]] = bounds;
  const midLat = (s + n) / 2;
  const midLng = (w + e) / 2;
  return {
    n: [n, midLng],
    s: [s, midLng],
    e: [midLat, e],
    w: [midLat, w],
  };
};

export const scaleBoundsFromCorner = (bounds, corner, dragLatLng) => {
  const [[s, w], [n, e]] = bounds;
  const latSpan = n - s;
  const lngSpan = e - w;
  const anchor = cornerPositions(bounds)[OPPOSITE_CORNER[corner]];
  const dLat = Math.abs(dragLatLng[0] - anchor[0]);
  const dLng = Math.abs(dragLatLng[1] - anchor[1]);
  const factor = Math.max(
    dLat / latSpan,
    dLng / lngSpan,
    MIN_SPAN / latSpan,
    MIN_SPAN / lngSpan
  );
  const latDir = corner === "nw" || corner === "ne" ? 1 : -1;
  const lngDir = corner === "ne" || corner === "se" ? 1 : -1;
  const MIN_PAD = MIN_SPAN * (1 + 1e-6);
  let targetLat = anchor[0] + latDir * latSpan * factor;
  let targetLng = anchor[1] + lngDir * lngSpan * factor;
  if (Math.abs(targetLat - anchor[0]) < MIN_SPAN) {
    targetLat = anchor[0] + latDir * MIN_PAD;
  }
  if (Math.abs(targetLng - anchor[1]) < MIN_SPAN) {
    targetLng = anchor[1] + lngDir * MIN_PAD;
  }
  return [
    [Math.min(anchor[0], targetLat), Math.min(anchor[1], targetLng)],
    [Math.max(anchor[0], targetLat), Math.max(anchor[1], targetLng)],
  ];
};

export const stretchBoundsEdge = (bounds, edge, dragLatLng) => {
  const [[s, w], [n, e]] = bounds;
  if (edge === "n") {
    return [[s, w], [Math.max(dragLatLng[0], s + MIN_SPAN), e]];
  }
  if (edge === "s") {
    return [[Math.min(dragLatLng[0], n - MIN_SPAN), w], [n, e]];
  }
  if (edge === "e") {
    return [[s, w], [n, Math.max(dragLatLng[1], w + MIN_SPAN)]];
  }
  return [[s, Math.min(dragLatLng[1], e - MIN_SPAN)], [n, e]];
};

const toRad = (deg) => (deg * Math.PI) / 180;

export const normalizeRotation = (deg) => ((deg % 360) + 360) % 360;

export const boundsCenter = (bounds) => [
  (bounds[0][0] + bounds[1][0]) / 2,
  (bounds[0][1] + bounds[1][1]) / 2,
];

export const rotatePointAroundCenter = (center, point, deg) => {
  const rad = toRad(deg);
  const cosLat = Math.cos(toRad(center[0]));
  const x = (point[1] - center[1]) * cosLat;
  const y = point[0] - center[0];
  const sin = Math.sin(rad);
  const cos = Math.cos(rad);
  return [
    center[0] + (-x * sin + y * cos),
    center[1] + (x * cos + y * sin) / cosLat,
  ];
};

const rotatePointMap = (points, bounds, deg) => {
  const center = boundsCenter(bounds);
  return Object.fromEntries(
    Object.entries(points).map(([key, point]) => [
      key,
      rotatePointAroundCenter(center, point, deg),
    ])
  );
};

export const rotatedCorners = (bounds, deg) =>
  rotatePointMap(cornerPositions(bounds), bounds, deg);

export const rotatedEdgeMidpoints = (bounds, deg) =>
  rotatePointMap(edgeMidpoints(bounds), bounds, deg);

export const unrotatePoint = (bounds, deg, point) =>
  rotatePointAroundCenter(boundsCenter(bounds), point, -deg);

export const rotationHandlePosition = (bounds, deg) => {
  const [[s], [n]] = bounds;
  const center = boundsCenter(bounds);
  return rotatePointAroundCenter(
    center,
    [n + (n - s) * 0.08, center[1]],
    deg
  );
};

export const rotationFromHandleDrag = (bounds, point) => {
  const center = boundsCenter(bounds);
  const cosLat = Math.cos(toRad(center[0]));
  const x = (point[1] - center[1]) * cosLat;
  const y = point[0] - center[0];
  if (Math.hypot(x, y) < MIN_SPAN) return null;
  return normalizeRotation((Math.atan2(x, y) * 180) / Math.PI);
};
